import React from "react";
import {OrderEditorTabProps} from "@widgets/pos/order/OrderEditor";
import CardHeader from "peggirkit/dist/components/Card/CardHeading/CardHeader";
import {Button, Card, InputLabel, SimpleCardHeading, TextField, Toggle, toggleDescriptionLabel} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {useFormContext, useWatch} from "react-hook-form";
import {
    businessDetailsValidation,
    emptyBusinessDetails,
    PosOrderBusinessDetails,
    PosOrderForm,
} from "@/data/entities/order";
import {emptyToNull, getInputValidationProps} from "@/data/Validation";
import {emptyNewDeliveryAddress} from "@/data/entities/deliveryAddress";
import AddressFields from "@widgets/pos/order/customer/address/AddressFields";

type BusinessField = keyof typeof businessDetailsValidation;

/**
 * Optional business details of the order (on the receipt / invoice). Only the
 * organisation name is required, unless the order is delivered to the
 * business address.
 */
const BusinessDetailsCard = ({isPending}: OrderEditorTabProps) => {
    const intl = useIntl();
    const {register, setValue, getValues, formState: {errors}} = useFormContext<PosOrderForm>();
    const businessDetails = useWatch<PosOrderForm, "businessDetails">({name: "businessDetails"});
    const customer = useWatch<PosOrderForm, "customer">({name: "customer"});
    const deliveryAddressType = useWatch<PosOrderForm, "deliveryAddress.type">({name: "deliveryAddress.type"});
    const isEnabled = businessDetails !== null;

    const setBusinessDetails = (details: PosOrderBusinessDetails | null) => {
        setValue("businessDetails", details, {shouldDirty: true});
        // Without business details there is no business address to deliver to
        if (details === null && getValues("deliveryAddress.type") === "BUSINESS")
            setValue("deliveryAddress", emptyNewDeliveryAddress(), {shouldDirty: true});
    };

    const registerField = (field: BusinessField) => register(`businessDetails.${field}`, {
        ...getInputValidationProps(businessDetailsValidation[field], intl),
        setValueAs: emptyToNull,
    });

    return (
        <Card
            header={
                <CardHeader>
                    <SimpleCardHeading title={<FormattedMessage id={"businessDetails"}/>}/>
                </CardHeader>
            }
        >
            <div className={"flex flex-col gap-6"}>
                <Toggle
                    type={"checkbox"}
                    label={toggleDescriptionLabel(
                        <FormattedMessage id={"businessOrder"}/>,
                        <FormattedMessage id={"businessOrderDescription"}/>,
                    )}
                    checked={isEnabled}
                    onChange={e => setBusinessDetails(e.target.checked ? emptyBusinessDetails() : null)}
                    disabled={isPending}
                />

                {
                    isEnabled && customer?.businessDetails &&
                    <Button
                        color={"secondary"}
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    setBusinessDetails({...customer.businessDetails!});
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"useBusinessDetailsOfAccount"}/>
                    </Button>
                }

                {
                    isEnabled &&
                    <div className={"flex flex-col gap-6"}>
                        <div className={"w-full grid grid-cols-2 gap-4"}>
                            <TextField
                                containerClassname={"col-span-2"}
                                type={"text"}
                                label={
                                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                                        <FormattedMessage id={"organisationName"}/>
                                    </InputLabel>
                                }
                                disabled={isPending}
                                error={errors.businessDetails?.organisationName?.message}
                                {...registerField("organisationName")}
                            />
                            <TextField
                                containerClassname={"col-span-2"}
                                type={"email"}
                                autoComplete={"off"}
                                label={
                                    <InputLabel hint={<FormattedMessage id={"optional"}/>}>
                                        <FormattedMessage id={"emailAddress"}/>
                                    </InputLabel>
                                }
                                disabled={isPending}
                                error={errors.businessDetails?.emailAddress?.message}
                                {...registerField("emailAddress")}
                            />
                            <TextField
                                type={"text"}
                                label={
                                    <InputLabel hint={<FormattedMessage id={"optional"}/>}>
                                        <FormattedMessage id={"vatNumber"}/>
                                    </InputLabel>
                                }
                                disabled={isPending}
                                error={errors.businessDetails?.taxNumber?.message}
                                {...registerField("taxNumber")}
                            />
                            <TextField
                                type={"text"}
                                label={
                                    <InputLabel hint={<FormattedMessage id={"optional"}/>}>
                                        <FormattedMessage id={"cocNumber"}/>
                                    </InputLabel>
                                }
                                disabled={isPending}
                                error={errors.businessDetails?.cocNumber?.message}
                                {...registerField("cocNumber")}
                            />
                        </div>

                        <AddressFields
                            prefix={"businessDetails"}
                            required={deliveryAddressType === "BUSINESS"}
                            isPending={isPending}
                        />
                    </div>
                }
            </div>
        </Card>
    );
};

export default BusinessDetailsCard;